import React, { useState, useEffect } from 'react';
import { Plus, Box, Edit, Trash2, Wrench, AlertTriangle } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { equiposService } from '../services/api';
import { formatMoney, formatDate } from '../utils/formatters';
import { CATEGORIAS_EQUIPO, ESTADOS_EQUIPO } from '../utils/constants';
import toast from 'react-hot-toast';

const InventarioPage = () => {
  const [equipos, setEquipos] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    codigo: '', nombre: '', categoria: 'ASCENSORES', ubicacion: '', marca: '', modelo: '',
    numero_serie: '', fecha_adquisicion: '', valor_compra: '', proveedor: '', garantia_hasta: '',
    estado: 'OPERATIVO', observaciones: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [equiposRes, alertasRes] = await Promise.all([
        equiposService.getAll(),
        equiposService.getAlertasMantenimiento()
      ]);
      setEquipos(equiposRes.data.data || []);
      setAlertas(alertasRes.data.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await equiposService.update(selected.id, form);
        toast.success('Equipo actualizado');
      } else {
        await equiposService.create(form);
        toast.success('Equipo creado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (equipo) => {
    setSelected(equipo);
    setForm({
      codigo: equipo.codigo, nombre: equipo.nombre, categoria: equipo.categoria,
      ubicacion: equipo.ubicacion || '', marca: equipo.marca || '', modelo: equipo.modelo || '',
      numero_serie: equipo.numero_serie || '', fecha_adquisicion: equipo.fecha_adquisicion?.split('T')[0] || '',
      valor_compra: equipo.valor_compra || '', proveedor: equipo.proveedor || '',
      garantia_hasta: equipo.garantia_hasta?.split('T')[0] || '', estado: equipo.estado,
      observaciones: equipo.observaciones || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await equiposService.delete(selected.id);
      toast.success('Equipo dado de baja');
      setConfirmOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({
      codigo: '', nombre: '', categoria: 'ASCENSORES', ubicacion: '', marca: '', modelo: '',
      numero_serie: '', fecha_adquisicion: '', valor_compra: '', proveedor: '', garantia_hasta: '',
      estado: 'OPERATIVO', observaciones: ''
    });
  };

  const statusVariant = { OPERATIVO: 'success', EN_REPARACION: 'warning', FUERA_DE_SERVICIO: 'danger', DADO_DE_BAJA: 'default' };

  const columns = [
    { header: 'Código', render: (row) => <span className="font-mono text-emerald-400">{row.codigo}</span> },
    { header: 'Nombre', accessor: 'nombre' },
    { header: 'Categoría', render: (row) => <Badge variant="info">{CATEGORIAS_EQUIPO.find(c => c.value === row.categoria)?.label || row.categoria}</Badge> },
    { header: 'Ubicación', render: (row) => row.ubicacion || '-' },
    { header: 'Estado', render: (row) => <Badge variant={statusVariant[row.estado]}>{ESTADOS_EQUIPO.find(e => e.value === row.estado)?.label || row.estado}</Badge> },
    { header: 'Acciones', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"><Edit size={16} /></button>
        <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"><Trash2 size={16} /></button>
      </div>
    )}
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Inventario de Equipos</h1>
          <p className="text-slate-400">Gestión de equipos e instalaciones</p>
        </div>
        <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>Nuevo Equipo</Button>
      </div>

      {alertas.length > 0 && (
        <Card className="p-4 bg-amber-500/10 border-amber-500/30">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-amber-400" size={20} />
            <h3 className="font-semibold text-amber-400">Alertas de Mantenimiento</h3>
          </div>
          <div className="space-y-2">
            {alertas.slice(0, 3).map((eq, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-white">{eq.nombre}</span>
                <span className="text-amber-400">{eq.proximo_mantenimiento ? `Próximo: ${formatDate(eq.proximo_mantenimiento)}` : eq.estado}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20"><Box className="text-blue-400" size={20} /></div>
            <div>
              <p className="text-sm text-slate-400">Total Equipos</p>
              <p className="text-xl font-bold text-white">{equipos.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-400">Operativos</p>
          <p className="text-xl font-bold text-emerald-400">{equipos.filter(e => e.estado === 'OPERATIVO').length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-400">En Reparación</p>
          <p className="text-xl font-bold text-amber-400">{equipos.filter(e => e.estado === 'EN_REPARACION').length}</p>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <Table columns={columns} data={equipos} loading={loading} />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Equipo' : 'Nuevo Equipo'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Código" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} required />
            <Input label="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required className="md:col-span-2" />
            <Select label="Categoría" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} options={CATEGORIAS_EQUIPO} required />
            <Input label="Ubicación" value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })} />
            <Select label="Estado" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} options={ESTADOS_EQUIPO} />
            <Input label="Marca" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} />
            <Input label="Modelo" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
            <Input label="N° Serie" value={form.numero_serie} onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} />
            <Input label="Fecha Adquisición" type="date" value={form.fecha_adquisicion} onChange={(e) => setForm({ ...form, fecha_adquisicion: e.target.value })} />
            <Input label="Valor Compra (S/)" type="number" value={form.valor_compra} onChange={(e) => setForm({ ...form, valor_compra: e.target.value })} />
            <Input label="Garantía Hasta" type="date" value={form.garantia_hasta} onChange={(e) => setForm({ ...form, garantia_hasta: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Guardar'}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} title="Dar de Baja Equipo" message="¿Estás seguro de dar de baja este equipo?" />
    </div>
  );
};

export default InventarioPage;
